import { Box, Stack, Tooltip } from '@mui/material';

import CaseStatusChip from 'components/v2/common/caseStatusChip';

import type { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { getCaseStatusColor } from 'data/constants/caseStatus';
import { getClientStatusLabelKey } from 'helpers/applicationRetainers';
import { getInstallmentAmount } from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type { BillingInstallment, BillingRule } from 'types';
import { formatCurrency } from 'utils/formatters';

interface BillingScheduleBarProps {
    installments: BillingInstallment[];
    muted?: boolean;
}

export const BillingScheduleBar = ({ installments, muted = false }: BillingScheduleBarProps) => (
    <Stack
        direction="row"
        aria-hidden
        sx={{
            height: 6,
            borderRadius: 999,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            opacity: muted ? 0.55 : 1,
        }}
    >
        {installments.map((installment, index) => (
            <Box
                key={index}
                sx={{
                    width: `${installment.percentage}%`,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    bgcolor: getCaseStatusColor(installment.statuses[0] ?? ''),
                    '&:not(:last-of-type)': {
                        borderRight: '2px solid',
                        borderColor: 'background.paper',
                    },
                }}
            />
        ))}
    </Stack>
);

interface BillingScheduleChipsProps {
    rule: BillingRule;
    muted?: boolean;
}

export const BillingScheduleChips = ({ rule, muted = false }: BillingScheduleChipsProps) => {
    const { translate } = useTranslate();
    const statusName = (status: ClientApplicationStatus) =>
        translate(getClientStatusLabelKey(status) ?? status);

    return (
        <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ flexWrap: 'wrap', opacity: muted ? 0.7 : 1 }}
        >
            {rule.installments.map((installment, index) => (
                <Tooltip
                    key={index}
                    title={formatCurrency(
                        getInstallmentAmount(rule.price, installment.percentage),
                        rule.currency
                    )}
                >
                    <CaseStatusChip
                        status={installment.statuses[0] ?? ''}
                        label={`${installment.percentage}% · ${installment.statuses
                            .map(statusName)
                            .join(` ${translate('serviceProviderBilling.or')} `)}`}
                    />
                </Tooltip>
            ))}
        </Stack>
    );
};
